<template>
    <Drawer :visible="visible" :header="t('filters.title')" position="right" class="!w-full sm:!w-[450px]"
        @update:visible="$emit('update:visible', $event)">
        <div class="space-y-4">
            <!-- Reservation Type Filter -->
            <div class="flex flex-col gap-2">
                <label class="font-semibold">
                    {{ t('filters.calendar.reservationType') }}
                </label>
                <Select :model-value="filters.reservationType" :options="reservationTypeOptions" showClear
                    optionLabel="label" optionValue="value" :placeholder="t('filters.calendar.selectReservationType')"
                    class="w-full"
                    @update:model-value="$emit('update:filters', { ...filters, reservationType: $event })" />
            </div>

            <!-- Visitor Type Filter -->
            <div v-if="filters.reservationType !== ReservationType.Internal" class="flex flex-col gap-2">
                <label class="font-semibold">
                    {{ t('filters.calendar.visitorType') }}
                </label>
                <MultiSelect :model-value="filters.visitorTypes" :options="visitorTypeOptions" optionLabel="label"
                    optionValue="value" :placeholder="t('filters.calendar.selectVisitorTypes')" :maxSelectedLabels="2"
                    class="w-full" display="chip"
                    @update:model-value="$emit('update:filters', { ...filters, visitorTypes: $event })" />
            </div>

            <Divider />

            <!-- Resource Filter -->
            <div class="flex flex-col gap-2">
                <label class="font-semibold">
                    {{ t('filters.calendar.resources') }}
                </label>
                <MultiSelect :model-value="filters.resourceIds" :options="filteredResources" optionLabel="name"
                    optionValue="id" :placeholder="t('filters.calendar.selectResources')" :maxSelectedLabels="2"
                    class="w-full" display="chip"
                    @update:model-value="$emit('update:filters', { ...filters, resourceIds: $event })" />
            </div>

            <Divider />

            <!-- My Reservations Filter -->
            <div class="flex flex-col gap-2">
                <div class="flex items-center">
                    <Checkbox :model-value="filters.showOnlyMyReservations" inputId="myReservations" :binary="true"
                        @update:model-value="$emit('update:filters', { ...filters, showOnlyMyReservations: $event })" />
                    <label for="myReservations" class="ml-2 cursor-pointer">
                        {{ t('filters.calendar.onlyMyReservations') }}
                    </label>
                </div>
            </div>

            <Divider />

            <!-- Show Maintenance Filter -->
            <div class="flex flex-col gap-2">
                <div class="flex items-center">
                    <Checkbox :model-value="filters.showMaintenance" inputId="showMaintenance" :binary="true"
                        @update:model-value="$emit('update:filters', { ...filters, showMaintenance: $event })" />
                    <label for="showMaintenance" class="ml-2 cursor-pointer">
                        {{ t('filters.calendar.showMaintenance') }}
                    </label>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-3">
                <Button v-if="hasActiveFilters" @click="$emit('clearFilters')" :label="t('filters.clear')"
                    icon="fa-solid fa-filter-circle-xmark" severity="secondary" text />
                <div v-else></div>
                <Button @click="$emit('update:visible', false)" :label="t('close')" severity="primary" />
            </div>
        </template>
    </Drawer>
</template>

<script setup lang="ts">
import type { Resource } from '@/lib/types/resource';
import type { ResourceType } from '@/lib/types/resourceType';
import { VisitorType } from '@/lib/types/visitor';
import type { CalendarFilters } from '@/types/calendarFilters';
import { ReservationType } from '@/types/calendarFilters';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
    visible: boolean;
    filters: CalendarFilters;
    resourceTypes: ResourceType[];
    filteredResources: Resource[];
    hasActiveFilters: boolean;
}

defineProps<Props>();

defineEmits<{
    'update:visible': [value: boolean];
    'update:filters': [value: CalendarFilters];
    clearFilters: [];
}>();

const visitorTypeOptions = computed(() => [
    { label: t('visitor.type.nonProfessional'), value: VisitorType.NonProfessional },
    { label: t('visitor.type.newProfessional'), value: VisitorType.NewProfessional },
    { label: t('visitor.type.erpProfessional'), value: VisitorType.ErpProfessional }
]);

const reservationTypeOptions = computed(() => [
    { label: t('filters.calendar.reservationTypes.external'), value: ReservationType.External },
    { label: t('filters.calendar.reservationTypes.internal'), value: ReservationType.Internal }
]);
</script>
